import { Button } from "primereact/button";
import { motion } from "framer-motion";
import * as client from "../../client";
import styles from "./Comment.module.css";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CommentScope,
  useCommentThread,
  useLocalUser,
  useUsers,
} from "../../pages/specialty-assistant/data";
import { PantheonAvatarWithoutMenu } from "../avatar/Avatar";
import { displayNameForUser } from "../../util";
import React from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { CommentMessage } from "./CommentMessage";
import { MentionInput } from "./MentionInput";
import { decodeMentions, encodeMentions } from "./mentions";

// Comment emails link to the report with this param so the recipient lands
// directly in the conversation. Kept in sync with COMMENTS_LINK_SUFFIX in
// backend/pantheon/src/asgard/asgard/response_emails/comment_emails.py.
const OPEN_COMMENTS_PARAM = "comments";
const OPEN_COMMENTS_VALUE = "open";
// Loss-run comment emails link to the loss-run tab with the policy year here, so
// that year's thread auto-opens. Kept in sync with LOSS_RUN_COMMENTS_PARAM in
// backend/pantheon/src/asgard/asgard/response_emails/comment_emails.py.
const OPEN_LOSS_RUN_COMMENTS_PARAM = "lossRunComments";
// Attachment comment emails link to the attachments tab with the attachment
// name here, so that attachment's thread auto-opens. Kept in sync with
// ATTACHMENT_COMMENTS_PARAM in
// backend/pantheon/src/asgard/asgard/response_emails/comment_emails.py.
const OPEN_ATTACHMENT_COMMENTS_PARAM = "attachmentComments";

type CommentContainerProps = {
  reportName: string;
  reportId: string;
  // Auto-open the panel when the URL has ?comments=open. Only the report
  // page header should pass this — on the dashboard, many rows render this
  // component and they would all try to open.
  autoOpenFromUrl?: boolean;
  // Which thread this is. Defaults to the report-level conversation; pass a
  // loss_year scope to attach the same UI to a single loss-run policy year.
  scope?: CommentScope;
};

const isEdited = (comment: client.Comment): boolean => {
  return comment.last_updated_ts !== comment.created_at_ts;
};

export const CommentContainer: React.FC<CommentContainerProps> = ({
  reportName,
  reportId,
  autoOpenFromUrl = false,
  scope = { kind: "report" },
}) => {
  const containerVariants = {
    rest: {},
    hover: { scale: 1.05, boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)" },
  };

  const overlayPanel = useRef<OverlayPanel>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const {
    commentsQuery,
    comments: allComments,
    add: addComment,
    remove: deleteCommentMutation,
    edit: editCommentMutation,
  } = useCommentThread(reportId, scope);
  const users = useUsers();
  const localUser = useLocalUser();
  const mentionableUsers = users.data?.all_company_users ?? [];

  // Auto-open from a mention-email link. Waits until the comments have loaded
  // so the page has settled, and retries across renders (via the effect deps)
  // in case the refs aren't attached on the first pass during initial page
  // load. A loss-year thread opens when the URL names its policy year; the
  // report-level panel opens on ?comments=open.
  const shouldAutoOpen =
    scope.kind === "loss_year"
      ? searchParams.get(OPEN_LOSS_RUN_COMMENTS_PARAM) === scope.policyYear
      : scope.kind === "attachment"
        ? searchParams.get(OPEN_ATTACHMENT_COMMENTS_PARAM) ===
          scope.attachmentName
        : autoOpenFromUrl &&
          searchParams.get(OPEN_COMMENTS_PARAM) === OPEN_COMMENTS_VALUE;
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (!shouldAutoOpen || hasAutoOpened.current || !commentsQuery.isSuccess) {
      return;
    }
    const panel = overlayPanel.current;
    const container = containerRef.current;
    if (!panel || !container) {
      return;
    }
    // Defer a frame so the panel aligns against the settled layout. The
    // consumed flag is set inside the callback so a cancelled frame (effect
    // re-run before paint) doesn't burn the single shot.
    const frame = requestAnimationFrame(() => {
      hasAutoOpened.current = true;
      panel.show(null, container);
    });
    return () => cancelAnimationFrame(frame);
  }, [shouldAutoOpen, commentsQuery.isSuccess]);

  const onAddComment = async () => {
    if (newComment) {
      // The composer shows display text ("@Jane Doe"); the canonical
      // "@[Jane Doe](id)" tokens are substituted in at save time.
      await addComment.mutateAsync(
        encodeMentions(newComment, mentionableUsers)
      );
      setNewComment("");
    }
  };

  const onDelete = (commentId: string) => {
    confirmDialog({
      message: "Are you sure you want to delete this comment?",
      header: "Delete Comment",
      icon: "pi pi-trash",
      acceptClassName: "p-button-danger",
      accept: () => deleteCommentMutation.mutate(commentId),
    });
  };

  const onStartEdit = (comment: client.Comment) => {
    setEditingId(comment.id);
    setEditText(decodeMentions(comment.message));
  };

  const onSaveEdit = async () => {
    if (editingId && editText) {
      await editCommentMutation.mutateAsync({
        commentId: editingId,
        message: encodeMentions(editText, mentionableUsers),
      });
      setEditingId(null);
      setEditText("");
    }
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const isOwnComment = (comment: client.Comment) =>
    localUser?.id === comment.user_id;

  return (
    <motion.div
      ref={containerRef}
      variants={containerVariants}
      className={styles.buttonContainer}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
      initial="rest"
      whileHover="hover"
    >
      <Button
        className={styles.mainButton}
        icon="pi pi-comment"
        size="small"
        severity="secondary"
        text
        onClick={(e) => {
          if (overlayPanel.current) {
            overlayPanel.current.toggle(e);
          }
        }}
      >
        {allComments.length > 0 && (
          <Badge value={allComments.length} severity="info" />
        )}
        <OverlayPanel
          ref={overlayPanel}
          showCloseIcon
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            marginLeft: "20px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.commentListContainer}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                marginBottom: "12px",
              }}
            >
              {scope.kind === "loss_year"
                ? `${scope.policyYear} Comments`
                : `${reportName} Comments`}
            </div>
            {allComments.map((comment) => {
              const user = users.data?.all_company_users.find(
                (user) => user.id === comment.user_id
              );
              const postTime = new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
              }).format(new Date(comment.created_at_ts));
              const editing = editingId === comment.id;

              return (
                <div key={comment.id} className={styles.commentRow}>
                  <div className={styles.horizontalContainer}>
                    <PantheonAvatarWithoutMenu
                      label={displayNameForUser(user)}
                      imageUrl={user?.profile_image_url}
                      showName={true}
                    />
                    <div className={styles.timeText}>
                      {postTime}
                      {isEdited(comment) && (
                        <span className={styles.editedIndicator}>
                          {" "}
                          (edited)
                        </span>
                      )}
                    </div>
                    {isOwnComment(comment) && !editing && (
                      <div className={styles.commentActions}>
                        <Button
                          icon="pi pi-pencil"
                          text
                          severity="secondary"
                          size="small"
                          className={styles.actionButton}
                          onClick={() => onStartEdit(comment)}
                          tooltip="Edit"
                          tooltipOptions={{ position: "top" }}
                        />
                        <Button
                          icon="pi pi-trash"
                          text
                          severity="danger"
                          size="small"
                          className={styles.actionButton}
                          onClick={() => onDelete(comment.id)}
                          tooltip="Delete"
                          tooltipOptions={{ position: "top" }}
                        />
                      </div>
                    )}
                  </div>
                  {editing ? (
                    <div className={styles.editContainer}>
                      <MentionInput
                        value={editText}
                        onChange={setEditText}
                        users={mentionableUsers}
                        excludeUserId={localUser?.id}
                        onKeyDown={(e) => {
                          if (
                            editText &&
                            e.key === "Enter" &&
                            (e.shiftKey || e.metaKey || e.ctrlKey)
                          ) {
                            onSaveEdit();
                          }
                          if (e.key === "Escape") {
                            onCancelEdit();
                          }
                        }}
                        autoResize
                        style={{ width: "100%" }}
                        disabled={editCommentMutation.isPending}
                      />
                      <div className={styles.editActions}>
                        <Button
                          label="Cancel"
                          text
                          size="small"
                          severity="secondary"
                          onClick={onCancelEdit}
                          disabled={editCommentMutation.isPending}
                        />
                        <Button
                          label="Save"
                          size="small"
                          onClick={onSaveEdit}
                          loading={editCommentMutation.isPending}
                          disabled={!editText || editCommentMutation.isPending}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.commentMessage}>
                      <CommentMessage message={comment.message} />
                    </div>
                  )}
                </div>
              );
            })}
            <div className={styles.horizontalContainer}>
              <PantheonAvatarWithoutMenu
                label={displayNameForUser(localUser)}
                imageUrl={localUser?.profile_image_url}
              />
              <MentionInput
                placeholder="Add a comment... (@ to mention)"
                rows={1}
                onChange={setNewComment}
                users={mentionableUsers}
                excludeUserId={localUser?.id}
                onKeyDown={(e) => {
                  if (
                    newComment &&
                    e.key == "Enter" &&
                    (e.shiftKey || e.metaKey || e.ctrlKey)
                  ) {
                    onAddComment();
                  }
                }}
                value={newComment}
                style={{ flexGrow: "1", margin: "2px" }}
                disabled={addComment.isPending}
                autoResize
              />
              <Button
                loading={addComment.isPending}
                disabled={!newComment || addComment.isPending}
                icon="pi pi-arrow-circle-up"
                onClick={onAddComment}
                style={{ height: "28px", width: "28px" }}
              />
            </div>
          </div>
        </OverlayPanel>
      </Button>
    </motion.div>
  );
};
